import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useAppDispatch } from 'hooks';

import { Button, ContainerHeader, ContainerLoading, ErrorResponse } from '~components';
import { faFileLines } from '~components/icon';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { setViewType } from '~main/reducers/app';

import { useGetTemplateQuery } from '~forms/actions/templates';
import { useResolveMergeFieldsMutation, useCreateCompletedMutation } from '~forms/actions/completedForms';

import {
  buildFieldSnapshot,
  extractFormTokens,
  isEditableField,
  resolveDateDefault,
  resolveTokens,
} from '~forms/lib/resolvedFieldValue';

import FillFieldRenderer from './FillFieldRenderer';

const FormFillPage: React.FC = () => {
  // form template id is a UUID — read it from the route (AppDynamicRoute only uses the param as a
  // remount key, it does NOT pass it as a prop), and never parseInt it.
  const { id: templateId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const clientIdStr = searchParams.get('clientId');
  const clientId = clientIdStr ? Number(clientIdStr) : null;

  // Hide the staff navigation chrome (header / quick menu) while filling — the device is handed to the
  // client. Reuses the existing `viewType: 'feature'` mechanism (Home applies `.prevent-nav`). Restore
  // on unmount so returning to the app (e.g. the completed-form view) shows the menus again.
  useEffect(() => {
    dispatch(setViewType('feature'));
    return () => {
      dispatch(setViewType(null));
    };
  }, [dispatch]);

  // ── Data loading ───────────────────────────────────────────────────────────
  const {
    data: template,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
    error: templateError,
  } = useGetTemplateQuery(templateId ?? '', { skip: !templateId });

  // ── Pick latest published version ─────────────────────────────────────────
  const publishedVersion = useMemo(() => {
    if (!template?.versions) return null;
    const published = template.versions
      .filter((v) => v.published_at)
      .sort((a, b) => b.version_number - a.version_number);
    return published[0] ?? null;
  }, [template]);

  // ── Merge token resolution ─────────────────────────────────────────────────
  const [resolveMergeFields] = useResolveMergeFieldsMutation();
  const [resolved, setResolved] = useState<Record<string, string>>({});
  // `resolveSettled` is the single source of truth for "resolution is done" (success OR failure OR
  // nothing to resolve). Seeding and the form render gate on it so nothing is ever built against a
  // half-populated `resolved` map — the bug that previously blanked seeded merge values.
  const [resolveSettled, setResolveSettled] = useState(false);

  useEffect(() => {
    if (!publishedVersion) {
      // Version not loaded yet: do NOT settle. Settling `true` while the version is still loading lets the
      // seeding effect run in the SAME commit (it reads the pre-update `resolveSettled`) and seed tokenised
      // defaults against an empty `resolved` map before resolution starts. Stay unsettled until we have a
      // concrete version to resolve against.
      setResolveSettled(false);
      setResolved({});
      return;
    }

    if (!clientId) {
      // Version present but no client context: nothing to resolve. Settle so the seeding effect
      // isn't deadlocked behind the render guard (line ~196) — keeps the two invariants independent.
      setResolved({});
      setResolveSettled(true);
      return;
    }

    const tokens = extractFormTokens(publishedVersion.field_schema);
    if (tokens.length === 0) {
      setResolved({});
      setResolveSettled(true);
      return;
    }

    setResolveSettled(false);
    resolveMergeFields({ context: { type: 'client', id: clientId }, fields: tokens })
      .unwrap()
      .then((data) => setResolved(data))
      .catch(() => {
        // Non-fatal: merge fields won't be resolved, but the page can still function.
        setResolved({});
      })
      .finally(() => setResolveSettled(true));
  }, [publishedVersion?.id, clientId]); // intentionally narrow deps — mutation ref is stable

  // ── Editable field responses state ────────────────────────────────────────
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [responsesSeeded, setResponsesSeeded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Seed editable defaults once, only AFTER resolution has settled — so merge tokens in a field's
  // default_value are substituted with their resolved values (not stripped to blank).
  useEffect(() => {
    if (!publishedVersion || responsesSeeded || !resolveSettled) return;

    const initialResponses: Record<string, string> = {};
    for (const field of publishedVersion.field_schema.fields) {
      if (!isEditableField(field)) continue;
      if (field.type === 'checkbox') {
        initialResponses[field.id] = resolveTokens(field.default_value ?? 'false', resolved);
      } else if (field.type === 'date') {
        // 'today'/'yesterday' keywords → a concrete YYYY-MM-DD the date input accepts.
        initialResponses[field.id] = resolveDateDefault(field.default_value);
      } else if (field.type === 'signature') {
        // Signature fields never carry a default_value, so seed explicitly rather than falling through to
        // the generic branch below, so a future default_value on this type can't pre-populate junk text.
        initialResponses[field.id] = '';
      } else {
        initialResponses[field.id] = resolveTokens(field.default_value ?? '', resolved);
      }
    }
    setResponses(initialResponses);
    setResponsesSeeded(true);
  }, [publishedVersion, resolved, resolveSettled, responsesSeeded]);

  const handleChange = useCallback((fieldId: string, newValue: string) => {
    setDirty(true);
    setResponses((prev) => ({ ...prev, [fieldId]: newValue }));
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const [createCompleted, { isLoading: isSubmitting }] = useCreateCompletedMutation();

  const handleSave = useCallback(async () => {
    if (!publishedVersion || !templateId || !clientId) return;

    // Validate required editable fields
    const newErrors: Record<string, string> = {};
    for (const field of publishedVersion.field_schema.fields) {
      if (!isEditableField(field)) continue;
      if (!field.required) continue;
      const val = responses[field.id] ?? '';
      if (field.type === 'checkbox') {
        // Checkboxes are seeded with the string 'false' (never empty), so the trim() === '' check below
        // would always pass. A required checkbox is a consent/confirmation gate — it must be ticked.
        if (val !== 'true') {
          newErrors[field.id] = 'This field is required';
        }
        continue;
      }
      if (val.trim() === '') {
        newErrors[field.id] = 'This field is required';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast('danger', 'Please complete all required fields');
      return;
    }

    // Full snapshot: every field's submitted value (editable answers + resolved read-only / merge /
    // rich-text / divider values) so the completed record is frozen against future merge-field changes.
    const field_responses = buildFieldSnapshot(publishedVersion.field_schema.fields, responses, resolved);

    try {
      const created = await createCompleted({
        template_id: templateId,
        template_version_id: publishedVersion.id,
        client_id: clientId,
        field_responses,
        status: 'submitted',
      }).unwrap();

      showToast('success', 'Form submitted');
      navigate(`/forms/completed/${created.id}`);
    } catch (err) {
      showToast('danger', getErrorMessage(err));
    }
  }, [publishedVersion, templateId, clientId, responses, resolved, createCompleted, showToast, navigate]);

  // ── Cancel / discard ─────────────────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    // Guard against losing partially-entered answers; if nothing was typed, leave immediately.
    if (dirty) {
      setConfirmDiscardOpen(true);
    } else {
      navigate('/forms');
    }
  }, [dirty, navigate]);

  const handleConfirmDiscard = useCallback(() => {
    setConfirmDiscardOpen(false);
    navigate('/forms');
  }, [navigate]);

  // ── Render guards ──────────────────────────────────────────────────────────
  if (!templateId) {
    return <ErrorResponse message="No template ID provided" />;
  }

  if (!clientId) {
    return <ErrorResponse message="No client selected. Please go back and select a client." />;
  }

  if (isTemplateLoading) {
    return <ContainerLoading loading />;
  }

  if (isTemplateError) {
    return <ErrorResponse message={getErrorMessage(templateError)} />;
  }

  if (!template) {
    return <ErrorResponse message="Template not found" />;
  }

  if (!publishedVersion) {
    return <ErrorResponse message="No published version found for this form. Please publish the form before filling it." />;
  }

  const { fields } = publishedVersion.field_schema;

  return (
    <div className="py-2 px-4">
      <ContainerHeader icon={faFileLines} iconSize="1x" className="info" dataCy="form-fill-header">
        Fill Form - {template.title}
      </ContainerHeader>

      {!responsesSeeded && <ContainerLoading loading />}

      {responsesSeeded && (
        <div className="form-fill">
          {fields.length === 0 && <p className="text-muted">This form has no fields.</p>}
          {fields.map((field) => (
            <FillFieldRenderer
              key={field.id}
              field={field}
              resolved={resolved}
              value={responses[field.id] ?? ''}
              onChange={handleChange}
              error={errors[field.id]}
            />
          ))}

          <div className="mt-3 d-flex justify-content-end gap-2">
            <Button color="info" outline onClick={handleCancel} disabled={isSubmitting} dataCy="form-fill-cancel-btn">
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleSave}
              loading={isSubmitting}
              disabled={isSubmitting}
              dataCy="form-fill-submit-btn"
            >
              Save
            </Button>
          </div>
        </div>
      )}

      <Modal isOpen={confirmDiscardOpen} toggle={() => setConfirmDiscardOpen(false)} data-cy="form-fill-discard-modal">
        <ModalHeader toggle={() => setConfirmDiscardOpen(false)}>Discard this form?</ModalHeader>
        <ModalBody>You have unsaved answers. If you leave now, the information entered will be lost.</ModalBody>
        <ModalFooter>
          <Button color="info" outline onClick={() => setConfirmDiscardOpen(false)} dataCy="form-fill-discard-cancel-btn">
            Keep filling
          </Button>
          <Button color="danger" onClick={handleConfirmDiscard} dataCy="form-fill-discard-confirm-btn">
            Discard
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default FormFillPage;
